<template>
  <div class="form-contact-product row-3 p-top-0 p-bot-2">
    <!-- Intro -->
    <div class="form-contact-product__intro">
      <h1
        class="title-big4 is-medium-bold"
        v-html="$t('form.productContact')"
      />
    </div>

    <!-- Form -->
    <div class="form-contact-product__form">
      <FormKit
        v-model="requestData"
        type="form"
        :actions="false"
        @submit="onSubmit"
        id="requestForm"
      >
        <div class="form-contact-product__grid">
          <FormContactProductSearch
            @select="onSelect"
            class="search-section"
            :searchedProducts="searchedProducts"
          />
          <div class="intro-form-section">
            <h3 class="title-base3" v-html="$t('form.yourData')" />
          </div>
          <!-- Products -->
          <FormKit
            type="hidden"
            name="requestedProducts"
            validation="required"
            id="input_requestedProducts"
          />

          <!-- Name -->
          <FormKit
            type="text"
            name="name"
            validation="required"
            :label="$t('form.name')"
            id="input_name"
            autocomplete="given-name"
          />

          <!-- Surname -->
          <FormKit
            type="text"
            name="surname"
            validation="required"
            :label="$t('form.surname')"
            id="input_surname"
            autocomplete="family-name"
          />

          <!-- Email -->
          <FormKit
            type="email"
            name="email"
            validation="required|email"
            :label="$t('form.email')"
            id="input_email"
            autocomplete="email"
          />

          <!-- Telephone -->
          <FormKit
            type="text"
            name="telephone"
            validation="required"
            :label="$t('form.phone')"
            id="input_telephone"
            autocomplete="tel"
          />

          <!-- City -->
          <FormKit
            type="text"
            name="city"
            validation="required"
            :label="$t('form.city')"
            id="input_city"
            autocomplete="address-level2"
          />

          <!-- Role -->
          <FormKit
            type="select"
            name="options"
            select-icon="down"
            :label="$t('form.role')"
            :options="$t('form.roles').split(',')"
            id="input_role"
            autocomplete="organization-title"
          />

          <!-- Messaggio -->
          <FormKit
            type="textarea"
            name="message"
            rows="7"
            :label="$t('form.message')"
            id="input_message"
            autocomplete="off"
          />
        </div>

        <!-- CHECKBOX Privacy -->
        <FormKit
          type="checkbox"
          name="terms"
          validation="accepted"
          :validation-messages="{ accepted: $t('form.termsError') }"
          id="input_terms"
          autocomplete="off"
        >
          <template #label="context">
            <p
              class="text-small3"
              v-html="
                $t('form.termsExtLabel', {
                  url: localePath({
                    name: 'privacy-policy',
                  }),
                })
              "
            />
          </template>
        </FormKit>

        <!-- CHECKBOX Newsletter -->
        <!--
        <FormKit
          type="checkbox"
          name="newsletter"
          id="input_newsletter"
          autocomplete="off"
        >
          <template #label="context">
            <p class="text-small3" v-html="$t('form.newsletterExtLabel')" />
          </template>
        </FormKit>
        -->

        <p class="text-small3" v-html="$t('form.endMessage')" />

        <div class="form-contact-product__cta">
          <button type="submit" class="round-icon-btn">
            {{ $t('form.sendRequest') }}
          </button>
        </div>

        <div class="form-contact-product__messages">
          <div v-if="successMessage" class="success-message">
            {{ $t('form.success') }}
          </div>
          <div v-if="errorMessage" class="error-message">
            {{ $t('form.error') }}
          </div>
        </div>
      </FormKit>
    </div>
  </div>
</template>

<script setup>
import { load } from 'recaptcha-v3'
import { reset } from '@formkit/core'

const emit = defineEmits(['contacts-success', 'contacts-failure'])

const props = defineProps({
  product: Object,
})

const runtimeConfig = useRuntimeConfig()
const { t } = useI18n()

const requestData = ref(null)
const loading = ref(false)
const successMessage = ref(false)
const errorMessage = ref(false)

const searchedProducts = ref([props.product.title])

const onSelect = (result) => {
  searchedProducts.value = [...new Set([...searchedProducts.value, result])]
}

watch(
  () => searchedProducts.value,
  (searchedProducts) => {
    requestData.value = {
      ...requestData.value,
      requestedProducts: searchedProducts.join(),
    }
  },
  { immediate: true },
)

const postinApiId = t('form.apiContactFormPostinId')

const onSubmit = (formkitData, node) => {
  node.clearErrors()

  loading.value = true
  successMessage.value = false
  errorMessage.value = false

  load(runtimeConfig.public.recaptchaKey).then((recaptcha) => {
    recaptcha.hideBadge()

    recaptcha.execute('postin').then((token) => {
      const formData = new FormData()

      formData.append('captcha', token)
      formData.append('api_id', postinApiId)

      formData.append('name', formkitData.name)
      formData.append('surname', formkitData.surname)
      formData.append('email', formkitData.email)
      formData.append('telephone', formkitData.telephone)
      formData.append('city', formkitData.city)
      formData.append('options', formkitData.options)
      formData.append('requestedProducts', formkitData.requestedProducts)
      formData.append('message', formkitData.message)

      formData.append('newsletter', formkitData.newsletter)

      // ID del template mail di postin
      fetch(runtimeConfig.public.postinURL, {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          loading.value = false

          if (response.status === 200) {
            successMessage.value = true
            emit('contacts-success')
            reset(node)
          } else {
            errorMessage.value = true
            emit('contacts-failure')
          }
        })
        .catch((e) => {
          loading.value = false
          errorMessage.value = true
          emit('contacts-failure')
        })
    })
  })
}
</script>

<script>
export default {
  name: 'FormContactProduct',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
